import moment from 'moment';
import 'moment/locale/de';
import { useEffect, useReducer, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
// import 'typeface-roboto';
import AppRoutes, { Paths } from './Routes';
import ConsentSettingsButton from './components/ConsentSettings/ConsentSettingsButton';
import { ErrorBoundary } from './components/ErrorBoundary/ErrorBoundary';
import AlertModal from './components/Modals/AlertModal/AlertModal';
import AlertContext, { AlertOnHide, AlertType, initialAlertState } from './context/alertContext';
import { alertReducer } from './context/alertReducer';
import AppPropsContext, { IConsentManagement, InitialAppPropsState } from './context/appPropsContext';
import { appPropsReducer } from './context/appPropsReducer';
import CheckInContext, { initialCheckInState } from './context/checkInContext';
import { checkinReducer } from './context/checkInReducer';
import DokumenteContext, { initialDokumenteState } from './context/dokumenteContext';
import { dokumenteReducer } from './context/dokumenteReducer';
import ExecuteRuleContext, { initialExecuteRuleState } from './context/executeRuleContext';
import { executeRuleReducer } from './context/executeRuleReducer';
import IFrameContext, { initialIFrameState } from './context/iframeContext';
import { iframeReducer } from './context/iframeReducer';
import LoginContext, { initialLoginState } from './context/loginContext';
import { loginReducer } from './context/loginReducer';
import QuestionnaireContext, { initialQuestionnaireState } from './context/questionnaireContext';
import { questionnaireReducer } from './context/questionnaireReducer';
import { sigpadReducer } from './context/sigPadReducer';
import SigPadContext, { initialSigPadState } from './context/sigpadContext';
import TerminierungContext, { initialTerminierungState } from './context/terminierungContext';
import { terminierungReducer } from './context/terminierungReducer';
import ThemeContext, { initialThemeState } from './context/themeContext';
import { themeReducer } from './context/themeReducer';
import VideochatContext, { initialVideochatState } from './context/videochatContext';
import { videochatReducer } from './context/videochatReducer';
import GlobalStyles from './globals/GlobalStyles';
import { getAppVersion, getThemeConfig } from './globals/global';
import { getTheme } from './globals/theme';
import { useLanguage } from './hooks/useLanguage';
import { getAppProperties } from './services/RestServices';
import ConsentAutoOpen from './thirdParty/consent/ConsentAutoOpen';
import { ConsentProvider } from './thirdParty/consent/ConsentProvider';
import { enableEyeAble, IEyeAbleConfig } from './thirdParty/eyeable/eyeable';
import { TrackingInit } from './tracking/TrackingInit';
import { showAlertMessage } from './utils/alertHandling';
import { setRuleProperties } from './utils/executeRuleUtils';
import { setDataFromAppProperties } from './utils/pcpUtils';
import { getQueryParams } from './utils/urlUtils';

window.app = window.app || {};

function App() {
    const navigate = useNavigate();
    moment.locale('de');

    const [state, dispatch] = useReducer(loginReducer, initialLoginState);
    const [alertstate, alertdispatch] = useReducer(alertReducer, initialAlertState);
    const [vsstate, vsdispatch] = useReducer(videochatReducer, initialVideochatState);
    const [tmstate, tmdispatch] = useReducer(terminierungReducer, initialTerminierungState);
    const [docstate, docdispatch] = useReducer(dokumenteReducer, initialDokumenteState);
    const [qstate, qdispatch] = useReducer(questionnaireReducer, initialQuestionnaireState);
    const [apstate, apdispatch] = useReducer(appPropsReducer, InitialAppPropsState);
    const [themestate, themedispatch] = useReducer(themeReducer, initialThemeState);
    const [executerulestate, executeruledispatch] = useReducer(executeRuleReducer, initialExecuteRuleState);
    const [sigpadstate, sigpaddispatch] = useReducer(sigpadReducer, initialSigPadState);
    const [iframestate, iframedispatch] = useReducer(iframeReducer, initialIFrameState);
    const [checkinstate, checkindispatch] = useReducer(checkinReducer, initialCheckInState);
    const [consentManagerConfig, setConsentManagerConfig] = useState<IConsentManagement>({
        enabled: false,
        vendor: 'none',
    });
    const [gaId, setGaId] = useState<string | undefined>();
    const [gtmId, setGtmId] = useState<string | undefined>();

    const location = useLocation();

    const { getSystemLanguageData, setSystemLanguage } = useLanguage();

    const refreshPage = () => {
        window.location.reload();
    };

    const getProperties = async () => {
        const propRes = await getAppProperties();
        if (propRes) {
            setDataFromAppProperties(propRes, apdispatch);

            // /* Google Tag Manager / Google Analytics ID Init */
            // if (propRes.googleTagManagerID) {
            //     initGTM(propRes.googleTagManagerID);
            // } else if (propRes.googleAnalyticsID) {
            //     ReactGA.initialize(propRes.googleAnalyticsID);
            // }

            /* ### Consent Manager ### */
            const cmp = propRes.consentManagement;
            if (cmp && cmp.enabled && (cmp.vendor === 'usercentrics' || cmp.vendor === 'consentmanagerde')) {
                setConsentManagerConfig(cmp);
                // 2) IDs nur vormerken – tatsächliche Initialisierung übernimmt TrackingInit
                if (propRes.googleTagManagerID) setGtmId(propRes.googleTagManagerID);
                if (propRes.googleAnalyticsID) setGaId(propRes.googleAnalyticsID);
            } else {
                // Kein CMP -> GA/GTM dürfen NICHT feuern
                setConsentManagerConfig({ enabled: false, vendor: 'none' });
                setGtmId(undefined);
                setGaId(undefined);
            }

            /* ### Eyable Widget (NOCH IM TEST) ### */
            const eyeableAcc: IEyeAbleConfig = {
                enabled: false, // ZUM TESTEN ENABLEN, MUSS DANN VOM BACKEND KOMMEN
                configUrl: 'https://cdn.eye-able.com/configs/example.com.js', //Kundenspezifische Config
                mainUrl: undefined, //Optional eigene Hosting-URL
                cspNonce: undefined, //falls CSP Nonces genutzt werden
            };

            enableEyeAble(eyeableAcc);
        } else {
            showAlertMessage({
                alertTitle: 'Fehler beim Laden der App',
                alertTxt:
                    'Die Seite konnte nicht geladen werden. Bitte versuchen Sie es zu einem späteren Zeitpunkt erneut.',
                alertType: AlertType.error,
                onHide: AlertOnHide.custom,
                onHideCustom: refreshPage,
                alertdispatch: alertdispatch,
                buttonName: 'Erneut versuchen',
            });
        }
    };

    useEffect(() => {
        if (!(window as any).app.language) {
            const systemLanguage = getSystemLanguageData();
            (window as any).app.language = systemLanguage.id;
            setSystemLanguage();
        }
        /* AppProperties */
        getProperties();

        /* allow Vidochat messages */
        window.app.allowVideochatMessages = true;
        // const hashParams = getQueryParams(location.hash);
        const hashParams = getQueryParams(location.search);
        if (hashParams.type === 'Redirect') {
            if (state.redirectUser === '') {
                dispatch({
                    type: 'SETREDIRECTUSER',
                    redirectUser: hashParams.user,
                    redirectPwd: hashParams.pwd,
                });
                // history.replace(Paths.HOME);
                navigate(Paths.HOME, { replace: true });
            }
        }
        if (hashParams.type === 'gdt') {
            if (hashParams.id && hashParams.id.length > 0) {
                dispatch({
                    type: 'SETGDTDATA',
                    gdt: {
                        id: hashParams.id,
                    },
                });
            }
        }
        if (location.hash.lastIndexOf('#/ExecuteRule') > -1) {
            if (hashParams.type === 'ExecuteRule') {
                setRuleProperties(hashParams, executeruledispatch);
                // history.replace(Paths.HOME);
                navigate(Paths.HOME, { replace: true });
            }
        }
        let theme = 'PCP';
        if (hashParams.theme) {
            const pTheme = hashParams.theme;
            if (pTheme.length > 0) {
                theme = pTheme;
            }
        } else {
            theme = getThemeConfig();
        }
        themedispatch({
            type: 'SETTHEME',
            theme: theme,
        });
    }, []);

    return (
        <ErrorBoundary>
            <ConsentProvider config={consentManagerConfig}>
                <ThemeProvider theme={getTheme(themestate.theme)}>
                    <GlobalStyles />
                    {/* Tracking laden nach den Regeln oben */}
                    <TrackingInit
                        gaId={gaId}
                        gtmId={gtmId}
                        cmpEnabled={consentManagerConfig.enabled && consentManagerConfig.vendor !== 'none'}
                    />
                    {/* Auto-Open: im DEV gern erzwingen; im PROD nur, wenn keine explizite Entscheidung vorliegt */}
                    <ConsentAutoOpen
                        enabled={consentManagerConfig.enabled && consentManagerConfig.vendor !== 'none'}
                        open="preferences" // oder 'welcome'
                        devForce={process.env.NODE_ENV === 'development'} // DEV: einmalig automatisch öffnen
                        resetBeforeOpen={false} // DEV optional true
                        debug={true}
                    />
                    <AlertContext.Provider value={{ alertstate, alertdispatch }}>
                        <LoginContext.Provider value={{ state, dispatch }}>
                            <VideochatContext.Provider value={{ vsstate, vsdispatch }}>
                                <TerminierungContext.Provider value={{ tmstate, tmdispatch }}>
                                    <DokumenteContext.Provider value={{ docstate, docdispatch }}>
                                        <QuestionnaireContext.Provider value={{ qstate, qdispatch }}>
                                            <AppPropsContext.Provider value={{ apstate, apdispatch }}>
                                                <ThemeContext.Provider value={{ themestate, themedispatch }}>
                                                    <CheckInContext.Provider value={{ checkinstate, checkindispatch }}>
                                                        <ExecuteRuleContext.Provider
                                                            value={{ executerulestate, executeruledispatch }}
                                                        >
                                                            <SigPadContext.Provider
                                                                value={{ sigpadstate, sigpaddispatch }}
                                                            >
                                                                <IFrameContext.Provider
                                                                    value={{ iframestate, iframedispatch }}
                                                                >
                                                                    <div
                                                                        className="App"
                                                                        data-appversion={getAppVersion()}
                                                                    >
                                                                        <AlertModal />
                                                                        <AppRoutes />
                                                                        {consentManagerConfig.enabled &&
                                                                            consentManagerConfig.vendor !== 'none' && (
                                                                                <ConsentSettingsButton />
                                                                            )}
                                                                    </div>
                                                                </IFrameContext.Provider>
                                                            </SigPadContext.Provider>
                                                        </ExecuteRuleContext.Provider>
                                                    </CheckInContext.Provider>
                                                </ThemeContext.Provider>
                                            </AppPropsContext.Provider>
                                        </QuestionnaireContext.Provider>
                                    </DokumenteContext.Provider>
                                </TerminierungContext.Provider>
                            </VideochatContext.Provider>
                        </LoginContext.Provider>
                    </AlertContext.Provider>
                </ThemeProvider>
            </ConsentProvider>
        </ErrorBoundary>
    );
}

export default App;
